import { Injectable, OnDestroy, Signal, signal } from '@angular/core';
import { SocketScannerService } from './socket-scanner/socket-scanner.service';
import { ScannerAppService } from './scanner-app.service';
import { BehaviorSubject, EMPTY, firstValueFrom, from, merge, Observable, Subject } from 'rxjs';
import { catchError, concatMap, distinctUntilChanged, switchMap, takeUntil, tap } from 'rxjs/operators';
import { PrinterService } from './printer.service';
import { ReceiptPrinterModeTypes } from '@shared/constants/receipt-printer-mode-types';
import { ReceiptPrintersService } from './receipt-printers';
import { NotifyPopupsService } from './notify-popups.service';
import { LogService } from './logger/log.service';
import { ISunmiService } from '@core/services/system/devices/sunmi/sunmi.service';
import { SunmiScannerPlugin } from '@core/capacitor-plugins/capacitor-plugins.class';
import { MyPosService } from './devices/my-pos/my-pos.service';
import { MyPosFacade } from '@features/my-pos/my-pos.facade';
import { MyPosScannerType } from '@core/capacitor-plugins/capacitor-plugins.class';
import { IAdyenService } from '@core/services/system/devices/adyen/adyen.service';
import { AdyenScannerService } from '@core/services/system/adyen/services/adyen-scanner.service';

/**
 * Aggregates every barcode source into a single stream and drives the hardware scanners.
 *
 * Design:
 *  - `newBarcodeEvent` is a pure query (no side effects). Reading it never (re)starts hardware.
 *  - `start()` / `stop()` are the only commands; they publish the desired on/off state.
 *  - Passive sources (socket / scanner-app / Star reader events) are merged once for the
 *    service lifetime — see subscribeToBarcodeSources().
 *  - The Star native reader is toggled through a serialized pipeline (concatMap) so a late
 *    stop can never override a fresh start, and repeated start()/stop() are no-ops.
 *  - Active-source scanners (Sunmi / myPOS / Adyen) are subscribe/unsubscribe driven and
 *    torn down on stop() via switchMap.
 */
@Injectable()
export class BarcodeScannerService implements OnDestroy {
  private readonly _newBarcodeEvent = new Subject<string>();
  private showNotify = true;
  private readonly logger = this.logService.createLogger('BarcodeScannerService');

  /** Desired running state of the hardware scanners; latest intent wins. */
  private readonly desiredOn$ = new BehaviorSubject<boolean>(false);
  private readonly destroy$ = new Subject<void>();

  /** Whether the native Star reader is actually running — avoids stopping a reader that was
   *  never started (that native call can hang and would then block the serialized queue).
   *  Reflects the real hardware state (set true only after the native start resolves). */
  private readonly _starReaderActive = signal(false);

  /** Public read-only view for components (e.g. to reflect scanner status in the UI). */
  public readonly starReaderActive: Signal<boolean> = this._starReaderActive.asReadonly();

  constructor(
    private socketScannerService: SocketScannerService,
    private scannerAppService: ScannerAppService,
    private printerService: PrinterService,
    private notifyPopupsService: NotifyPopupsService,
    private receiptPrintersService: ReceiptPrintersService,
    private myPosService: MyPosService,
    private myPosFacade: MyPosFacade,
    private logService: LogService,
    private readonly sunmiService: ISunmiService,
    private readonly adyenService: IAdyenService,
    private readonly adyenScanner: AdyenScannerService
  ) {
    this.subscribeToBarcodeSources();
    this.subscribeToStarReaderLifecycle();
    this.subscribeToActiveSourceScanners();
  }

  ngOnDestroy(): void {
    this.desiredOn$.next(false);
    this.destroy$.next();
    this.destroy$.complete();
    this.desiredOn$.complete();
    this._newBarcodeEvent.complete();
  }

  /** Query (CQS): aggregated barcode stream. Pure — reading it has no side effects. */
  public get newBarcodeEvent(): Observable<string> {
    return this._newBarcodeEvent.asObservable();
  }

  /** Command: request the hardware scanners to be running. Idempotent. */
  public start(): void {
    this.desiredOn$.next(true);
  }

  /** Command: request the hardware scanners to be stopped. Idempotent. */
  public stop(): void {
    this.desiredOn$.next(false);
  }

  /** Query (CQS): whether the hardware scanners are currently requested to run. */
  public get isStarted(): boolean {
    return this.desiredOn$.getValue();
  }

  /**
   * Passive barcode sources (already-running streams) merged once into `_newBarcodeEvent`
   * for the whole service lifetime — not re-created on every start/stop.
   */
  private subscribeToBarcodeSources(): void {
    merge(
      this.socketScannerService.onData(),
      this.scannerAppService.newBarcodeEvent,
      this.receiptPrintersService.newBarcodeEvent
    )
      .pipe(takeUntil(this.destroy$))
      .subscribe((barcode) => {
        this._newBarcodeEvent.next(barcode);
      });
  }

  /**
   * Star native reader on/off, serialized. `distinctUntilChanged` makes repeated
   * start()/stop() no-ops; `concatMap` runs each transition to completion in order, so a
   * stop enqueued right after a start can never land before the start has finished.
   */
  private subscribeToStarReaderLifecycle(): void {
    this.desiredOn$
      .pipe(
        distinctUntilChanged(),
        concatMap((on) => from(this.setStarReader(on))),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }

  /**
   * Active-source scanners (Sunmi / myPOS / Adyen): actively started, results fed into
   * `_newBarcodeEvent`. `switchMap` tears them down on stop(). No async-ordering hazard
   * here — these are observable subscribe/unsubscribe, unlike the promise-based Star reader.
   */
  private subscribeToActiveSourceScanners(): void {
    this.desiredOn$
      .pipe(
        distinctUntilChanged(),
        switchMap((on) => (on ? this.activeSourceScanners$() : EMPTY)),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }

  private setStarReader(on: boolean): Promise<void> {
    return on ? this.enableStarReader() : this.disableStarReader();
  }

  private async enableStarReader(): Promise<void> {
    const printerList = await firstValueFrom(
      this.printerService.getPrinterListOnce(ReceiptPrinterModeTypes.STAR_SCANNER)
    ).catch(() => []);
    if (!printerList.length) {
      this.showNotify = true;
      return;
    }
    if (this.showNotify) {
      this.notifyPopupsService.launchNotification('add-start-scanner');
      this.showNotify = false;
    }
    await this.receiptPrintersService.startBarcodeReader(printerList[0]);
    this._starReaderActive.set(true);
  }

  private async disableStarReader(): Promise<void> {
    // Never issue a native stop for a reader that isn't running: the initial desired=false
    // (and any redundant stop) would otherwise hang the native call and wedge the concatMap
    // queue, so a subsequent start() would never run.
    if (!this._starReaderActive()) {
      return;
    }
    this._starReaderActive.set(false);
    try {
      await this.receiptPrintersService.stopBarcodeReader();
    } catch (err) {
      this.logger.error(err, 'disableStarReader:stopBarcodeReader');
    }
  }

  private activeSourceScanners$(): Observable<unknown> {
    // `*.startScanner(...)` is typed `any` by the plugin stub, so `tap` can't infer its
    // element type — the result params are annotated explicitly with the emitted shape.
    const scanners: Observable<unknown>[] = [];

    if (this.sunmiService.isSunmiDevice && this.sunmiService.hasBroadcastScanner) {
      const sunmi$ = SunmiScannerPlugin.startScanner(this.sunmiService.recommendedScannerType).pipe(
        tap((result: { value: string }) => this._newBarcodeEvent.next(result.value)),
        catchError((err) => {
          this.logger.error(err, 'activeSourceScanners:SunmiScannerPlugin:startScanner');
          return EMPTY;
        })
      );
      scanners.push(sunmi$);
    }

    if (this.myPosService.isTargetDeviceMyPos && this.myPosService.hasBroadcastScanner) {
      const myPos$ = this.myPosFacade.startScanner(MyPosScannerType.Broadcast).pipe(
        tap((result: { value: string }) => this._newBarcodeEvent.next(result.value)),
        catchError((err) => {
          this.logger.error(err, 'activeSourceScanners:myPosFacade:startScanner');
          return EMPTY;
        })
      );
      scanners.push(myPos$);
    }

    if (this.adyenService.isScannerConnected) {
      const adyen$ = this.adyenScanner.startScanner().pipe(
        tap((result: { code: string }) => this._newBarcodeEvent.next(result.code)),
        catchError((err) => {
          this.logger.error(err, 'activeSourceScanners:adyen:startScanner');
          return EMPTY;
        })
      );
      scanners.push(adyen$);
    }

    return scanners.length ? merge(...scanners) : EMPTY;
  }
}
